import { CreateGroupNodeFn, RuleGroupNode, RuleGroupOp, RuleLeafBase, isRuleGroupNode } from './types';

export const DEFAULT_RULE_MAX_CONDITIONS_PER_LEVEL = 3;
export const DEFAULT_RULE_MAX_GROUP_NESTING_DEPTH = 3;

export function maxGroupNodeDepth<TLeaf extends RuleLeafBase>(node: RuleGroupNode<TLeaf>, currentDepth = 0): number {
  let maxDepth = currentDepth;
  for (const child of node.children) {
    if (!isRuleGroupNode(child)) continue;
    maxDepth = Math.max(maxDepth, maxGroupNodeDepth(child, currentDepth + 1));
  }
  return maxDepth;
}

function getSelectedChildIndices<TLeaf extends RuleLeafBase>(
  parent: RuleGroupNode<TLeaf>,
  selectedIds: Set<string>,
): number[] {
  return parent.children.map((child, index) => (selectedIds.has(child.id) ? index : -1)).filter((index) => index >= 0);
}

export function areIndicesContiguous(indices: number[]): boolean {
  if (indices.length < 2) return false;

  const sorted = [...indices].sort((left, right) => left - right);
  for (let index = 1; index < sorted.length; index += 1) {
    if (sorted[index] !== sorted[index - 1] + 1) {
      return false;
    }
  }

  return true;
}

export function canGroupSelectedChildren<TLeaf extends RuleLeafBase>(
  parent: RuleGroupNode<TLeaf>,
  parentDepth: number,
  selectedIds: Set<string>,
  maxNestingDepth: number,
  createGroupNode: CreateGroupNodeFn<TLeaf>,
): boolean {
  if (selectedIds.size < 2) return false;

  const indices = getSelectedChildIndices(parent, selectedIds).sort((left, right) => left - right);
  if (indices.length < 2) return false;

  const isContiguous = indices.every((index, position) => index === indices[0] + position);
  if (!isContiguous) return false;

  const start = indices[0];
  const end = indices[indices.length - 1];
  const selectedChildren = parent.children.slice(start, end + 1);
  const wrapped = createGroupNode(selectedChildren, parent.op);

  return maxGroupNodeDepth(wrapped, parentDepth + 1) <= maxNestingDepth;
}

export function canSetGroupConnectorOp<TLeaf extends RuleLeafBase>(
  group: RuleGroupNode<TLeaf>,
  childIndex: number,
  newOp: RuleGroupOp,
  parentDepth: number,
  maxNestingDepth: number,
  createGroupNode: CreateGroupNodeFn<TLeaf>,
): boolean {
  if (childIndex <= 0 || childIndex >= group.children.length) return false;
  if (newOp === group.op) return true;

  if (group.children.length === 2) return true;

  const left = group.children[childIndex - 1];
  const right = group.children[childIndex];
  const pairGroup = createGroupNode([left, right], newOp);

  if (maxGroupNodeDepth(pairGroup, parentDepth + 1) <= maxNestingDepth) {
    return true;
  }

  return true;
}

export function setGroupConnectorOp<TLeaf extends RuleLeafBase>(
  group: RuleGroupNode<TLeaf>,
  childIndex: number,
  newOp: RuleGroupOp,
  parentDepth: number,
  maxNestingDepth: number,
  createGroupNode: CreateGroupNodeFn<TLeaf>,
): RuleGroupNode<TLeaf> {
  if (childIndex <= 0 || childIndex >= group.children.length) return group;
  if (newOp === group.op) return group;
  if (!canSetGroupConnectorOp(group, childIndex, newOp, parentDepth, maxNestingDepth, createGroupNode)) {
    return group;
  }

  if (group.children.length === 2) {
    return { ...group, op: newOp };
  }

  const left = group.children[childIndex - 1];
  const right = group.children[childIndex];
  const pairGroup = createGroupNode([left, right], newOp);

  if (maxGroupNodeDepth(pairGroup, parentDepth + 1) <= maxNestingDepth) {
    return {
      ...group,
      children: [...group.children.slice(0, childIndex - 1), pairGroup, ...group.children.slice(childIndex + 1)],
    };
  }

  return { ...group, op: newOp };
}

export function wrapContiguousChildren<TLeaf extends RuleLeafBase>(
  parent: RuleGroupNode<TLeaf>,
  parentDepth: number,
  selectedIds: Set<string>,
  maxNestingDepth: number,
  createGroupNode: CreateGroupNodeFn<TLeaf>,
): RuleGroupNode<TLeaf> | null {
  if (!canGroupSelectedChildren(parent, parentDepth, selectedIds, maxNestingDepth, createGroupNode)) {
    return null;
  }

  const indices = getSelectedChildIndices(parent, selectedIds).sort((left, right) => left - right);
  const start = indices[0];
  const end = indices[indices.length - 1];
  const selectedChildren = parent.children.slice(start, end + 1);
  const wrapped = createGroupNode(selectedChildren, parent.op);

  return {
    ...parent,
    children: [...parent.children.slice(0, start), wrapped, ...parent.children.slice(end + 1)],
  };
}

export function updateRuleGroupInTree<TLeaf extends RuleLeafBase>(
  node: RuleGroupNode<TLeaf>,
  groupId: string,
  updater: (group: RuleGroupNode<TLeaf>) => RuleGroupNode<TLeaf>,
): RuleGroupNode<TLeaf> {
  if (node.id === groupId) return updater(node);

  return {
    ...node,
    children: node.children.map((child) =>
      isRuleGroupNode(child) ? updateRuleGroupInTree(child, groupId, updater) : child,
    ),
  };
}

export function findRuleGroupById<TLeaf extends RuleLeafBase>(
  node: RuleGroupNode<TLeaf>,
  groupId: string,
): RuleGroupNode<TLeaf> | null {
  if (node.id === groupId) return node;

  for (const child of node.children) {
    if (!isRuleGroupNode(child)) continue;
    const found = findRuleGroupById(child, groupId);
    if (found) return found;
  }

  return null;
}

export function findRuleGroupDepth<TLeaf extends RuleLeafBase>(
  node: RuleGroupNode<TLeaf>,
  groupId: string,
  currentDepth = 0,
): number | null {
  if (node.id === groupId) return currentDepth;

  for (const child of node.children) {
    if (!isRuleGroupNode(child)) continue;
    const depth = findRuleGroupDepth(child, groupId, currentDepth + 1);
    if (depth !== null) return depth;
  }

  return null;
}
